import React, { useEffect, useRef, useState } from "react";
import base from "../../styles/PantallaDeProgramasAcademicos.v2.module.css";

import { getInstitutions } from '../../api/institutions';
import { getCampus } from '../../api/campus';
import { getCycles } from '../../api/ciclosEscolares';
import { getPrograms } from '../../api/programs';
import { getSecciones } from '../../api/secciones';
import { getUsuarios } from '../../api/usuarios';

const defaultForm = {
  nombre_completo: "",
  email: "",
  telefono: "",
  fecha_nacimiento: "",
  municipio_residencia: "",
  nivel_estudios_actual: "",
  institucion_procedencia: "",
  como_se_entero: "",
  id_institucion: "",
  id_campus: "",
  id_seccion: "",
  id_programa: "",
  id_ciclo_ingreso: "",
  responsable_seguimiento: "",
  estado_prospecto: "NUEVO",
  comentarios: "",
};

const estadoOptions = [
  { value: 'NUEVO', label: 'Nuevo' },
  { value: 'SEGUIMIENTO', label: 'Seguimiento' },
  { value: 'EN_ESPERA', label: 'En Espera' },
  { value: 'EN_PROCESO', label: 'En Proceso' },
  { value: 'ACEPTADO', label: 'Aceptado' },
  { value: 'RECHAZADO', label: 'Rechazado' },
  { value: 'PERDIO_INTERES', label: 'Perdió Interés' },
  { value: 'MATRICULADO', label: 'Matriculado' },
  { value: 'INSCRITO', label: 'Inscrito' },
];

const comoSeEnteroOptions = [
  'Redes sociales',
  'Página web',
  'Recomendación',
  'Feria educativa',
  'Publicidad impresa',
  'Radio/TV',
  'Otro',
];

const nivelEstudiosOptions = [
  'Secundaria',
  'Bachillerato',
  'Técnico',
  'Licenciatura',
  'Maestría',
  'Doctorado',
  'Otro',
];

export default function NuevoInteresadoModal({ open, onClose, onSubmit, title = "Registrar Interesado", readOnly = false, initialForm = null }) {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const dialogRef = useRef(null);
  const [instituciones, setInstituciones] = useState([]);
  const [campusOptions, setCampusOptions] = useState([]);
  const [ciclosEscolares, setCiclosEscolares] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [responsables, setResponsables] = useState([]);

  const requiredFields = {
    nombre_completo: 'Nombre Completo',
    email: 'Email',
    telefono: 'Teléfono',
    fecha_nacimiento: 'Fecha de Nacimiento',
    nivel_estudios_actual: 'Nivel de Estudios',
    id_institucion: 'Institución',
    id_campus: 'Campus',
    id_ciclo_ingreso: 'Ciclo de Ingreso',
  };

  const validateForm = () => {
    const newErrors = {};
    Object.entries(requiredFields).forEach(([field, label]) => {
      if (!form[field] || form[field].toString().trim() === '') {
        newErrors[field] = `${label} es obligatorio`;
      }
    });
    // Validar email
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Email inválido';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (!open) return;
    getInstitutions().then(data => setInstituciones(data)).catch(() => setInstituciones([]));
    getCampus().then(data => setCampusOptions(data)).catch(() => setCampusOptions([]));
    getCycles().then(data => {
      const sorted = [...data].sort((a, b) => {
        const anioA = a.anio || parseInt((a.name || a.nombre_ciclo || '').split('-')[0]) || 0;
        const anioB = b.anio || parseInt((b.name || b.nombre_ciclo || '').split('-')[0]) || 0;
        return anioB - anioA;
      });
      setCiclosEscolares(sorted);
    }).catch(() => setCiclosEscolares([]));
    getPrograms().then(data => {
      setProgramas(Array.isArray(data) ? data : (data?.results || []));
    }).catch(() => setProgramas([]));
    getSecciones().then(data => setSecciones(Array.isArray(data) ? data : [])).catch(() => setSecciones([]));
    getUsuarios('ADMISIONES').then(data => {
      setResponsables(Array.isArray(data) ? data.filter((u) => u.activo) : []);
    }).catch(() => setResponsables([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      if (initialForm) {
        setForm({ ...defaultForm, ...initialForm });
      } else {
        setForm(defaultForm);
      }
      setErrors({});
      const first = dialogRef.current?.querySelector("input, select, textarea, button");
      first?.focus();
    }
  }, [open, initialForm]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      // Al cambiar la sección, invalidar el programa si ya no pertenece a ella
      if (name === 'id_seccion') {
        const prog = programas.find((p) => String(p.id || p.id_programa) === String(f.id_programa));
        if (value && prog && String(prog.sectionId) !== String(value)) {
          next.id_programa = '';
        }
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit?.({ ...form });
  };

  // Programas filtrados por la sección seleccionada (la sección vive en el programa)
  const programasFiltrados = form.id_seccion
    ? programas.filter((p) => String(p.sectionId) === String(form.id_seccion))
    : programas;

  return (
    <div className={base.modalBackdrop} role="presentation" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose?.();
    }}>
      <div className={base.modalContainer} role="dialog" aria-modal="true" aria-labelledby="interesado-title" ref={dialogRef}>
        <div className={base.modalHeader}>
          <h2 id="interesado-title" className={base.modalTitle}>{title}</h2>
          <button className={base.modalClose} aria-label="Cerrar" onClick={onClose}>✕</button>
        </div>

        <form className={base.modalBody} onSubmit={handleSubmit}>
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
            <div className={base.modalGrid}>
              {/* Columna izquierda - Datos personales */}
              <div className={base.formGroup}>
                <label className={base.label}>Nombre Completo <span style={{color:'red'}}>*</span></label>
                <input className={`${base.input} ${errors.nombre_completo ? base.inputError : ''}`} name="nombre_completo" value={form.nombre_completo} onChange={handleChange} placeholder="Nombre completo del interesado" />
                {errors.nombre_completo && <span style={{color:'red', fontSize:'12px'}}>{errors.nombre_completo}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Email <span style={{color:'red'}}>*</span></label>
                <input type="email" className={`${base.input} ${errors.email ? base.inputError : ''}`} name="email" value={form.email} onChange={handleChange} placeholder="correo@ejemplo.com" />
                {errors.email && <span style={{color:'red', fontSize:'12px'}}>{errors.email}</span>}
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Teléfono <span style={{color:'red'}}>*</span></label>
                <input className={`${base.input} ${errors.telefono ? base.inputError : ''}`} name="telefono" value={form.telefono} onChange={handleChange} placeholder="10 dígitos" maxLength={15} />
                {errors.telefono && <span style={{color:'red', fontSize:'12px'}}>{errors.telefono}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Fecha de Nacimiento <span style={{color:'red'}}>*</span></label>
                <input type="date" className={`${base.input} ${errors.fecha_nacimiento ? base.inputError : ''}`} name="fecha_nacimiento" value={form.fecha_nacimiento} onChange={handleChange} />
                {errors.fecha_nacimiento && <span style={{color:'red', fontSize:'12px'}}>{errors.fecha_nacimiento}</span>}
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Municipio de Residencia</label>
                <input className={base.input} name="municipio_residencia" value={form.municipio_residencia} onChange={handleChange} placeholder="Ciudad o municipio" />
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Nivel de Estudios Actual <span style={{color:'red'}}>*</span></label>
                <select className={`${base.select} ${errors.nivel_estudios_actual ? base.inputError : ''}`} name="nivel_estudios_actual" value={form.nivel_estudios_actual} onChange={handleChange}>
                  <option value="">— Selecciona nivel —</option>
                  {nivelEstudiosOptions.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                {errors.nivel_estudios_actual && <span style={{color:'red', fontSize:'12px'}}>{errors.nivel_estudios_actual}</span>}
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Institución de Procedencia</label>
                <input className={base.input} name="institucion_procedencia" value={form.institucion_procedencia} onChange={handleChange} placeholder="Escuela/Instituto de origen" />
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>¿Cómo se enteró?</label>
                <select className={base.select} name="como_se_entero" value={form.como_se_entero} onChange={handleChange}>
                  <option value="">— Selecciona —</option>
                  {comoSeEnteroOptions.map(o => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              {/* Datos institucionales */}
              <div className={base.formGroup}>
                <label className={base.label}>Institución <span style={{color:'red'}}>*</span></label>
                <select className={`${base.select} ${errors.id_institucion ? base.inputError : ''}`} name="id_institucion" value={form.id_institucion} onChange={handleChange}>
                  <option value="">— Selecciona institución —</option>
                  {Array.isArray(instituciones) && instituciones.map(inst => (
                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                  ))}
                </select>
                {errors.id_institucion && <span style={{color:'red', fontSize:'12px'}}>{errors.id_institucion}</span>}
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Campus <span style={{color:'red'}}>*</span></label>
                <select className={`${base.select} ${errors.id_campus ? base.inputError : ''}`} name="id_campus" value={form.id_campus} onChange={handleChange}>
                  <option value="">— Selecciona campus —</option>
                  {Array.isArray(campusOptions) && campusOptions.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
                {errors.id_campus && <span style={{color:'red', fontSize:'12px'}}>{errors.id_campus}</span>}
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Sección</label>
                <select className={base.select} name="id_seccion" value={form.id_seccion} onChange={handleChange}>
                  <option value="">— Selecciona sección —</option>
                  {Array.isArray(secciones) && secciones.map(s => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Programa de Interés</label>
                <select className={base.select} name="id_programa" value={form.id_programa} onChange={handleChange}>
                  <option value="">— Selecciona programa —</option>
                  {Array.isArray(programasFiltrados) && programasFiltrados.map(p => (
                    <option key={p.id || p.id_programa} value={p.id || p.id_programa}>{p.name || p.nombre_programa}</option>
                  ))}
                </select>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Ciclo Escolar de Ingreso <span style={{color:'red'}}>*</span></label>
                <select className={`${base.select} ${errors.id_ciclo_ingreso ? base.inputError : ''}`} name="id_ciclo_ingreso" value={form.id_ciclo_ingreso} onChange={handleChange}>
                  <option value="">— Selecciona ciclo —</option>
                  {Array.isArray(ciclosEscolares) && ciclosEscolares.map(ciclo => (
                    <option key={ciclo.id} value={ciclo.id}>{ciclo.name || ciclo.nombre_ciclo}</option>
                  ))}
                </select>
                {errors.id_ciclo_ingreso && <span style={{color:'red', fontSize:'12px'}}>{errors.id_ciclo_ingreso}</span>}
              </div>

              <div className={base.formGroup}>
                <label className={base.label}>Estado del Prospecto</label>
                <div className={base.statusSelectWrapper}>
                  <span className={`${base.statusDot} ${form.estado_prospecto === 'ACEPTADO' || form.estado_prospecto === 'MATRICULADO' ? base.dotGreen : form.estado_prospecto === 'RECHAZADO' || form.estado_prospecto === 'PERDIO_INTERES' ? base.dotRed : ''}`} style={!['ACEPTADO','MATRICULADO','RECHAZADO','PERDIO_INTERES'].includes(form.estado_prospecto) ? {background: '#f0ad4e'} : {}} />
                  <select className={base.select} name="estado_prospecto" value={form.estado_prospecto} onChange={handleChange} disabled={readOnly}>
                    {estadoOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={base.formGroup}>
                <label className={base.label}>Responsable de Seguimiento</label>
                <select className={base.select} name="responsable_seguimiento" value={form.responsable_seguimiento} onChange={handleChange}>
                  <option value="">— Selecciona responsable —</option>
                  {Array.isArray(responsables) && responsables.map(u => (
                    <option key={u.id} value={u.id}>{u.nombre}</option>
                  ))}
                </select>
              </div>
              <div className={base.formGroup} style={{ gridColumn: 'span 2' }}>
                <label className={base.label}>Comentarios</label>
                <textarea className={base.textarea || base.input} name="comentarios" value={form.comentarios} onChange={handleChange} rows={3} placeholder="Observaciones adicionales..." style={{ resize: 'vertical', minHeight: '60px' }} />
              </div>
            </div>
          </fieldset>

          <div className={base.modalActions}>
            {!readOnly && (<button type="submit" className={base.primaryButton}>GUARDAR</button>)}
            <button type="button" className={base.secondaryButton} onClick={onClose}>{readOnly ? 'CERRAR' : 'CANCELAR'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
